import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Building, MailCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { isAxiosError } from 'axios';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Self-service staff password reset request. POSTs the email to
 * /v1/auth/forgot-password and always shows the same generic confirmation —
 * the backend never reveals whether the email is registered (no enumeration).
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string>('');
  const [touched, setTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  function validate(value: string): string {
    if (!value.trim()) return 'Email address is required.';
    if (!EMAIL_RE.test(value)) return 'Please enter a valid email address.';
    return '';
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const validationError = validate(email);
    if (validationError) {
      setError(validationError);
      setTouched(true);
      return;
    }
    setError('');
    setIsSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      // Always show the generic success state regardless of whether the account
      // exists — mirrors the enumeration-safe server response.
      setSent(true);
    } catch (err) {
      let message = 'Something went wrong. Please try again.';
      if (isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 429) {
          message = 'Too many requests. Please wait a moment and try again.';
        } else if (status === 400) {
          message = 'Please enter a valid email address.';
        } else if (!err.response) {
          message = 'Network error. Please check your connection.';
        }
      }
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* ── Left decorative panel (hidden on mobile) ── */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-slate-900 to-slate-950 border-r border-slate-800">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-600">
            <Building size={20} className="text-white" aria-hidden="true" />
          </div>
          <span className="text-xl font-bold text-white tracking-tight">BestAcco PMS</span>
        </div>

        <div>
          <blockquote className="text-slate-300 text-lg font-light leading-relaxed">
            "Managing short-stay properties at scale, made simple."
          </blockquote>
          <p className="mt-4 text-slate-500 text-sm">
            Reservations &middot; Channel Management &middot; Dynamic Pricing &middot; Operations
          </p>
        </div>

        <p className="text-slate-600 text-xs">
          &copy; {new Date().getFullYear()} BestAcco. All rights reserved.
        </p>
      </div>

      {/* ── Form panel ── */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {/* Mobile brand */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary-600">
              <Building size={18} className="text-white" aria-hidden="true" />
            </div>
            <span className="text-lg font-bold text-white">BestAcco PMS</span>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            {sent ? (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
                  <MailCheck size={24} className="text-emerald-600" aria-hidden="true" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Check your inbox</h1>
                <p
                  role="status"
                  aria-live="polite"
                  className="mt-2 text-sm text-gray-500"
                >
                  If that email is registered, we&apos;ve sent a reset link — check your
                  inbox and follow the instructions to sign in.
                </p>
                <Link
                  to="/login"
                  className="mt-6 inline-block text-sm font-medium text-primary-600 hover:text-primary-500 transition-colors"
                >
                  Back to sign in
                </Link>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h1 className="text-2xl font-bold text-gray-900">Forgot password?</h1>
                  <p className="mt-1 text-sm text-gray-500">
                    Enter your account email and we&apos;ll send you a link to reset it.
                  </p>
                </div>

                {error && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700"
                  >
                    {error}
                  </div>
                )}

                <form
                  onSubmit={handleSubmit}
                  noValidate
                  aria-label="Forgot password form"
                  className="space-y-5"
                >
                  <Input
                    label="Email address"
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (touched) setError(validate(e.target.value));
                    }}
                    onBlur={() => {
                      setTouched(true);
                      setError(validate(email));
                    }}
                    error={touched ? error : undefined}
                    placeholder="you@example.com"
                    required
                    disabled={isSubmitting}
                  />

                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    size="lg"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending…' : 'Send reset link'}
                  </Button>
                </form>
              </>
            )}
          </div>

          {!sent && (
            <p className="mt-6 text-center text-sm text-slate-400">
              Remember your password?{' '}
              <Link
                to="/login"
                className="font-medium text-primary-400 hover:text-primary-300 transition-colors"
              >
                Sign in
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
